import React, { useCallback, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import { isNativeTagsEnabled } from '@disco/state-management/selectors/user'
import NativeTagsPopover from 'components/common/native-tags-popover'
import Button from 'components/ui/button'
import Tooltip from 'components/ui/tooltip'

type Props = {
  assignedTags: Record<string, any>[]
  buttonSize?: 'default' | 'compact'
  showLabel?: boolean
  trackIds: number[]
}

/**
 * Native tags menu component that provides tag management interface
 * Shows button to open tag popover with support for readonly mode
 * @param assignedTags - Currently assigned tags to tracks
 * @param buttonSize - Size of the menu button ('default' or 'compact')
 * @param showLabel - Whether to show button label text
 * @param trackIds - Array of track IDs to apply tags to
 */
function NativeTagsMenu({
  assignedTags,
  buttonSize,
  showLabel,
  trackIds,
}: Props): null | React.ReactNode {
  const nativeTagsEnabled: boolean = useSelector(isNativeTagsEnabled)
  const isNativeTagsReadonly: boolean = useSelector(
    state =>
      state.user.business?.private_metadata.is_native_tags_readonly ?? false,
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const toggle = useCallback(() => {
    setMenuOpen(m => !m)
  }, [])
  const close = useCallback(() => {
    setMenuOpen(false)
  }, [])
  if (!nativeTagsEnabled) return null
  const label = isNativeTagsReadonly ? (
    <span>Tag editing is not enabled for this DISCO.</span>
  ) : (
    <span>Add track tags</span>
  )
  let button

  if (showLabel) {
    button = (
      <Tooltip content={isNativeTagsReadonly ? label : null}>
        <Button
          appearance="link"
          caption="Add track tags"
          compact={buttonSize === 'compact'}
          disabled={isNativeTagsReadonly}
          icon="auto-tag"
          onClick={toggle}
        />
      </Tooltip>
    )
  } else {
    button = (
      <Tooltip content={label}>
        <Button
          appearance="icon"
          compact={buttonSize === 'compact'}
          disabled={isNativeTagsReadonly}
          icon="auto-tag"
          onClick={toggle}
        />
      </Tooltip>
    )
  }

  return (
    <>
      {button}
      {menuOpen && (
        <NativeTagsPopover
          trackIds={trackIds}
          assignedTags={assignedTags}
          onClose={close}
        />
      )}
    </>
  )
}

export default NativeTagsMenu
